import { sum, getFilteredStats } from "./helpers";

export const computeWinner = (nameA, statsA, nameB, statsB) => {
  const sA = getFilteredStats(statsA);
  const sB = getFilteredStats(statsB);
  if (!sA || !sB) return null;

  // ── metric definitions ─────────────────────────────────────────────────────
  // priority: 1 = decides the winner outright if there's a difference
  //           2 = tiebreaker if priority-1 metrics are tied
  //           3 = secondary tiebreaker only
  const metrics = [
    {
      key: "total_points_earned",
      label: "Total Points Earned",
      priority: 1, // decides winner alone if different
      a: sum(sA.states, "total_points_earned"),
      b: sum(sB.states, "total_points_earned"),
      format: (v) => Number(v.toFixed(2)).toLocaleString(),
    },
    {
      key: "total_points_redeemed",
      label: "Total Points Redeemed",
      priority: 2, // checked only if earnings are tied
      a: sum(sA.states, "total_points_redeemed"),
      b: sum(sB.states, "total_points_redeemed"),
      format: (v) => Number(v.toFixed(2)).toLocaleString(),
    },
    {
      key: "total_participants",
      label: "Total Participants",
      priority: 3, // checked only if 1+2 tied
      a: sum(sA.states, "total_participants"),
      b: sum(sB.states, "total_participants"),
      format: (v) => v.toLocaleString(),
    },
    {
      key: "total_scans",
      label: "Total Product Scans",
      priority: 4, // remaining metrics — net wins decides
      a: sum(sA.product_codes, "total_scans"),
      b: sum(sB.product_codes, "total_scans"),
      format: (v) => v.toLocaleString(),
    },
    {
      key: "unique_users",
      label: "Unique Users",
      priority: 4,
      a: sum(sA.product_codes, "unique_users"),
      b: sum(sB.product_codes, "unique_users"),
      format: (v) => v.toLocaleString(),
    },
    {
      key: "category_scans",
      label: "Total Category Scans",
      priority: 4,
      a: sum(sA.product_categories, "total_scans"),
      b: sum(sB.product_categories, "total_scans"),
      format: (v) => v.toLocaleString(),
    },
  ];

  // ── tag each metric with its row-level winner ──────────────────────────────
  const results = metrics.map((m) => {
    let winner = "tie";
    if (m.a > m.b) winner = "A";
    else if (m.b > m.a) winner = "B";
    return { ...m, winner };
  });

  // ── priority-based overall winner ──────────────────────────────────────────
  // Each metric has its own priority level. We walk them in order 1 → 2 → 3 → 4.
  // The FIRST metric that isn't a tie decides the winner outright.
  // Only if every single metric is tied do we call it a draw.
  //
  // Order:
  //   1 — Points Earned       (decides alone if different)
  //   2 — Points Redeemed     (only checked if earnings tied)
  //   3 — Participants        (only checked if 1+2 tied)
  //   4 — Scans / Users / Categories (checked last, net wins within group)

  let overall = "tie";
  let reason = "Both schemes performed equally across all metrics.";

  // Sort unique priority levels ascending so we check them in order
  const priorityLevels = [...new Set(metrics.map((m) => m.priority))].sort(
    (a, b) => a - b,
  );

  for (const level of priorityLevels) {
    const group = results.filter((m) => m.priority === level);

    if (group.length === 1) {
      // Single metric at this level — it alone decides
      const m = group[0];
      if (m.winner === "A") {
        overall = "A";
        reason = `higher ${m.label} (${m.format(m.a)} vs ${m.format(m.b)})`;
        break;
      } else if (m.winner === "B") {
        overall = "B";
        reason = `higher ${m.label} (${m.format(m.b)} vs ${m.format(m.a)})`;
        break;
      }
      // tied → fall through to next level
    } else {
      // Multiple metrics at this level — net wins within the group decides
      const winsA = group.filter((m) => m.winner === "A").length;
      const winsB = group.filter((m) => m.winner === "B").length;

      if (winsA > winsB) {
        overall = "A";
        reason = group
          .filter((m) => m.winner === "A")
          .map(
            (m) => `higher ${m.label} (${m.format(m.a)} vs ${m.format(m.b)})`,
          )
          .join(", ");
        break;
      } else if (winsB > winsA) {
        overall = "B";
        reason = group
          .filter((m) => m.winner === "B")
          .map(
            (m) => `higher ${m.label} (${m.format(m.b)} vs ${m.format(m.a)})`,
          )
          .join(", ");
        break;
      }
      // net-tied within group → fall through to next level
    }
  }

  // ── convenience score counts (used by WinnerBanner UI only) ───────────────
  // These reflect actual metric wins per side across ALL metrics,
  // purely for display purposes — the overall winner is set above.
  const scoreA = results.filter((m) => m.winner === "A").length;
  const scoreB = results.filter((m) => m.winner === "B").length;

  return { overall, scoreA, scoreB, nameA, nameB, metrics: results, reason };
};

